/* External */
import { useEffect, useRef, useState } from 'react';
import addSeconds from 'date-fns/addSeconds';
import { useDispatch } from 'react-redux';
import { isEqual } from 'date-fns';

/* Utils */
import useRequestAndDelete from './useRequestAndDelete';
import useCurrentAndNextSchedules from './useCurrentAndNextSchedules';
import { STATUS_TYPES } from '../utils/constants';
import { scheduleFetchChannelSchedules } from '../storage/channels';

// How long to wait before firing the individual lane dispatch.
// useFetchAvailableSchedules sends a BATCH request for all visible channels
// upfront. Delaying individual lane dispatches gives the batch a head start:
// if the batch response arrives before this delay, the schedules are already
// loaded and the individual dispatch is skipped entirely — eliminating duplicate
// requests. On slow TVs / slow networks, the delay is imperceptible to the user
// but significantly reduces dispatch storm pressure at EPG mount time.
const INDIVIDUAL_LANE_DISPATCH_DELAY_MS = 150;

// eslint-disable-next-line max-params -- we need to send 4 parameters
export default function useCurrentAndNextSchedulesAndFetch(
  channel,
  numberOfSchedules,
  timeToUse = undefined,
  toDatetime = undefined,
  hasLazyloading = false,
) {
  const currentAndNextSchedules = useCurrentAndNextSchedules(channel, numberOfSchedules, timeToUse);
  const { channelId, sourceProvider } = channel || {};
  const [requestId, setRequestId] = useState(undefined);
  const timeToUseRef = useRef();
  const toDatetimeRef = useRef();

  const request = useRequestAndDelete(requestId);
  const dispatch = useDispatch();
  // Holds the pending debounce timer so it can be cancelled when deps change
  // or on component unmount. Using a ref (not state) avoids extra re-renders.
  const dispatchTimerRef = useRef(null);

  const isFetching = (!request && !channel && !channelId)
    || request?.status === STATUS_TYPES.INIT_STATE;

  const isError = request && request?.status === STATUS_TYPES.FAILED_STATE;

  useEffect(() => {
    let condition = channelId && (
      !currentAndNextSchedules?.size
      || (currentAndNextSchedules?.size > 0 && currentAndNextSchedules?.size < numberOfSchedules));

    if (timeToUse && toDatetime) {
      condition = channelId && (
        !currentAndNextSchedules?.size
        || (currentAndNextSchedules?.size > 0 && currentAndNextSchedules?.size < numberOfSchedules)
      ) && (hasLazyloading && (!isEqual(timeToUseRef.current, timeToUse)
        || !isEqual(toDatetimeRef.current, toDatetime)));
    }

    if (condition) {
      timeToUseRef.current = timeToUse;
      toDatetimeRef.current = toDatetime;

      // Cancel any previously scheduled dispatch before scheduling a new one.
      // This handles rapid dep changes (e.g. filter switches, scroll jumps).
      if (dispatchTimerRef.current !== null) {
        clearTimeout(dispatchTimerRef.current);
      }

      // Debounce: delay the individual lane dispatch so that the batch request
      // from useFetchAvailableSchedules has time to fire first.
      //
      // WHY: useFetchAvailableSchedules dispatches ONE request covering ALL
      // visible channels. If that batch returns before INDIVIDUAL_LANE_DISPATCH_DELAY_MS,
      // currentAndNextSchedules will have data on the next effect run, the condition
      // above will be false, and no individual request is fired — eliminating
      // the duplicate. If the batch is slow, the individual request fires as a
      // fallback after the delay, preserving the current behavior.
      //
      // setTimeout/clearTimeout are supported in all embedded TV browsers.
      dispatchTimerRef.current = setTimeout(() => {
        dispatchTimerRef.current = undefined;

        let fromDatetime = timeToUseRef.current;
        if (currentAndNextSchedules?.size > 0 && !timeToUseRef.current) {
          const lastSchedule = currentAndNextSchedules.last();
          // We use fromDatetime as the endTime of last loaded schedule for this channel
          // and we add 1 second to avoid that last loaded schedule to be returned in response
          fromDatetime = addSeconds(lastSchedule.endTime, 1);
        }
        const action = scheduleFetchChannelSchedules({
          channelIdOrIds: channelId,
          schedulesForChannel:
            // Decrease numberOfSchedules to request by the size of the schedules we already have
            numberOfSchedules && (numberOfSchedules - (currentAndNextSchedules?.size || 0)),
          fromDatetime,
          toDatetime: toDatetimeRef.current,
          sourceProvider,
        });
        setRequestId(action.requestId);
        dispatch(action);
      }, INDIVIDUAL_LANE_DISPATCH_DELAY_MS);
    }

    return () => {
      // Cancel the pending dispatch if deps change before the timer fires
      // (e.g. schedules arrived from the batch → condition becomes false)
      // or when the component unmounts.
      if (dispatchTimerRef.current !== null || dispatchTimerRef.current !== undefined) {
        clearTimeout(dispatchTimerRef.current);
        dispatchTimerRef.current = undefined;
      }
    };
  }, [
    dispatch,
    currentAndNextSchedules,
    numberOfSchedules,
    channelId,
    sourceProvider,
    timeToUse,
    toDatetime,
    hasLazyloading,
  ]);

  return {
    currentAndNextSchedules,
    isFetching,
    isError,
    /* Contract inherited from the former useCurrentSchedule: the first
       schedule is available or the request has settled (success or failure).
       No request yet counts as incomplete, so callers keep showing
       skeletons until the first dispatch fires. */
    isCompleted: !!currentAndNextSchedules?.first()
      || !channelId
      || request?.status === STATUS_TYPES.SUCCEED_STATE
      || request?.status === STATUS_TYPES.FAILED_STATE,
  };
}
